/**
 * Модульный стор `Chat`-инстансов (AI SDK v7), живущих ВНЕ React-дерева.
 *
 * Зачем: `useChat({ id })` создаёт `new Chat` в `useRef` на каждый маунт —
 * при ремаунте компонента (поворот экрана меняет mobile↔desktop контейнер)
 * чат пересоздаётся и теряет ленту и активный стрим. Держим инстанс в
 * модульной Map, передаём в `useChat({ chat })` — ремаунты переиспользуют
 * тот же Chat, диалог и стрим переживают поворот/сворачивание.
 *
 * sessionId (для серверной персистентности) держим рядом с инстансом —
 * транспорт читает его на каждый запрос; startNewChat/loadSession его меняют.
 */

import { Chat } from '@ai-sdk/react';
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithToolCalls, type UIMessage } from 'ai';
import { useChatStore } from '../stores/chat-store';

interface Entry {
    chat:      Chat<UIMessage>;
    sessionId: string;
    /** Восстановление сообщений из БД для этого инстанса уже пытались? */
    restored:  boolean;
}

const instances = new Map<string, Entry>();

function newSessionId(): string {
    return crypto.randomUUID();
}

/** Активный sessionId переживает перезагрузку страницы через localStorage. */
function sessionKey(chatId: string): string {
    return `pflow.chat.session.${chatId}`;
}
function loadPersistedSessionId(chatId: string): string {
    try {
        return localStorage.getItem(sessionKey(chatId)) || newSessionId();
    } catch {
        return newSessionId();
    }
}
function persistSessionId(chatId: string, sessionId: string): void {
    try { localStorage.setItem(sessionKey(chatId), sessionId); } catch { /* приватный режим */ }
}

export function getProjectChat(owner: string, repo: string): { chat: Chat<UIMessage>; id: string } {
    const id = `${owner}/${repo}/chat`;
    let entry = instances.get(id);
    if (!entry) {
        // sessionId берём из localStorage — после reload продолжаем ту же сессию.
        const sessionId = loadPersistedSessionId(id);
        persistSessionId(id, sessionId);
        const chat = new Chat<UIMessage>({
            id,
            transport: new DefaultChatTransport({
                api:         `/api/repo/${owner}/${repo}/chat`,
                credentials: 'include',
                // body — функция: читает АКТУАЛЬНЫЙ контекст страницы и sessionId
                // на каждый запрос (пользователь мог уйти на /plan, сменить сессию).
                body: () => {
                    const ctx = useChatStore.getState().ctx;
                    return {
                        ref:         ctx?.gitRef,
                        path:        ctx?.path,
                        sessionId:   instances.get(id)?.sessionId,
                        pageContext: ctx?.pageContext,
                    };
                },
            }),
            sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
        });
        entry = { chat, sessionId, restored: false };
        instances.set(id, entry);
    }
    return { chat: entry.chat, id };
}

export function getSessionId(chatId: string): string | undefined {
    return instances.get(chatId)?.sessionId;
}

export function setSessionId(chatId: string, sessionId: string): void {
    const entry = instances.get(chatId);
    if (entry) entry.sessionId = sessionId;
    persistSessionId(chatId, sessionId);
}

export function rollSessionId(chatId: string): string {
    const sid = newSessionId();
    setSessionId(chatId, sid);
    return sid;
}

/** Одноразовый флаг: восстановление сообщений из БД уже пытались (после reload). */
export function markRestored(chatId: string): boolean {
    const entry = instances.get(chatId);
    if (!entry || entry.restored) return false;
    entry.restored = true;
    return true;
}
